
/*! swish-rebuy.js — centralized helpers for Rebuy widgets
   Owner: Swish Embassy (Dean)
   Version: 2025-08-26
   Notes:
   - Keep Rebuy "no-code": only paste a 1-liner in each widget's Custom JS to call SWISH_REBUY.afterRender(...)
   - Update this file only (hosted in Shopify Files, as a theme asset, or via GTM)
*/
(function () {
  const SWISH_REBUY = {
    version: "2025.08.26",
    cfg: {
      // REGION: auto-detect from Shopify localization if present; fallback "us"
      region: (window.localization && window.localization.country && window.localization.country.iso_code
              ? String(window.localization.country.iso_code).toLowerCase()
              : "us"),
      // FEATURE FLAGS: toggle behavior quickly without editing Rebuy widgets
      features: {
        availabilityGuard: true,    // gate Quick Add by region/stock
        capCards: 3,                // number of recs to show per widget (hidden beyond this)
        reorderByPrice: true,       // try to surface a low-priced add-on first in cart widget
        addThresholdNudge: true,    // show subtotal-to-free-shipping nudge (if detectable)
        trackAdds: true             // push dataLayer events for analytics
      },
      // DATA SOURCES (swap later without touching Rebuy):
      // If you move to a Printful proxy, replace availabilityEndpoint with that URL.
      availabilityMode: "static_json", // "static_json" | "proxy_api" | "allow_all"
      availabilityEndpoint: "https://cdn.shopify.com/s/files/1/0028/1852/files/SWISH_PRODUCT_AVAIL.json", // placeholder; update if needed
      complementsJson: null,       // optional URL to map designKey -> complements
      freeShippingThreshold: 100   // adjust per-region if desired
    },

    // ---- PUBLIC API (called from Rebuy widgets) ---------------------------------
    afterRender(slot, payload) {
      try {
        // Small delay to allow Rebuy to finish DOM mutation
        setTimeout(() => {
          if (slot === "pdp_complete") {
            this._wirePDP(payload);
          } else if (slot === "cart_cross_sell") {
            this._wireCart(payload);
          } else if (slot === "thank_you") {
            this._wireThankYou(payload);
          }
        }, 0);
      } catch (e) {
        console.warn("[SWISH_REBUY] afterRender error", e);
      }
    },

    // OPTIONAL: guard Quick Add (callable if Rebuy exposes a 'before add-to-cart' hook)
    onQuickAdd(lineItem) {
      const proceed = () => {
        if (this.cfg.features.trackAdds) {
          this._track("rebuy_quick_add", {
            slot: lineItem && lineItem.slot,
            productId: lineItem && (lineItem.variant_id || lineItem.product_id || lineItem.id),
          });
        }
        return true;
      };

      if (!this.cfg.features.availabilityGuard) return proceed();

      return this._availabilityCheck(lineItem).then((ok) => {
        if (!ok) {
          alert("That option isn’t available in your region right now.");
          return false;
        }
        return proceed();
      }).catch((err) => {
        console.warn("[SWISH_REBUY] availability check failed; allowing add", err);
        return proceed();
      });
    },

    // ---- INTERNALS --------------------------------------------------------------
    _wirePDP(/* payload */) {
      // Decorate PDP recommendation block(s)
      this._decorateCards('[data-rebuy-widget="pdp"] .rebuy-product-block, .rebuy-widget[data-location*="product"] .rebuy-product-block');
    },

    _wireCart(/* payload */) {
      // Decorate Cart Drawer recommendations and optionally reorder cards
      const sel = '[data-rebuy-widget="cart"] .rebuy-product-block, .rebuy-widget[data-location*="cart"] .rebuy-product-block';
      this._decorateCards(sel);

      if (this.cfg.features.reorderByPrice) {
        this._prioritizeLowPrice(sel);
      }
      if (this.cfg.features.addThresholdNudge) {
        this._thresholdNudge();
      }
    },

    _wireThankYou(/* payload */) {
      // Decorate Thank-You page upsells
      this._decorateCards('[data-rebuy-widget="thankyou"] .rebuy-product-block, .rebuy-widget[data-location*="post_purchase"] .rebuy-product-block');
    },

    _decorateCards(selector) {
      const cards = Array.from(document.querySelectorAll(selector));
      if (!cards.length) return;

      // Cap number of visible cards
      const cap = Number(this.cfg.features.capCards || 0);
      if (cap > 0 && cards.length > cap) {
        cards.slice(cap).forEach((c) => (c.style.display = "none"));
      }

      // Add unified class for styling (inherits theme styles)
      cards.forEach((c) => c.classList.add("swish-rebuy-card"));
    },

    _prioritizeLowPrice(selector) {
      // Try to move lowest-priced item to the first card (non-destructive)
      const container = document.querySelector(selector)?.closest(".rebuy-widget") || document.querySelector(selector)?.parentElement;
      if (!container) return;

      const cards = Array.from(container.querySelectorAll(".rebuy-product-block"));
      if (cards.length < 2) return;

      // Find lowest price among visible cards
      const parsePrice = (el) => {
        const priceEl = el.querySelector(".rebuy-price, [data-rebuy-price]");
        if (!priceEl) return Number.POSITIVE_INFINITY;
        const raw = (priceEl.getAttribute("data-rebuy-price") || priceEl.textContent || "").replace(/[^0-9.]/g, "");
        return parseFloat(raw || "0");
      };

      let minIdx = 0;
      let minVal = parsePrice(cards[0]);
      for (let i = 1; i < cards.length; i++) {
        const val = parsePrice(cards[i]);
        if (val < minVal) {
          minVal = val;
          minIdx = i;
        }
      }

      if (minIdx > 0) {
        container.insertBefore(cards[minIdx], cards[0]);
      }
    },

    _thresholdNudge() {
      // Attempts to read subtotal from Rebuy's DOM; if not available, skip
      const totalEl = document.querySelector("[data-rebuy-cart-total],[data-rebuy-subtotal]");
      if (!totalEl) return;

      const raw = totalEl.getAttribute("data-rebuy-cart-total") || totalEl.getAttribute("data-rebuy-subtotal") || totalEl.textContent || "";
      const subtotal = parseFloat((raw || "").replace(/[^0-9.]/g, ""));
      if (!isFinite(subtotal)) return;

      const threshold = this.cfg.freeShippingThreshold;
      const remaining = threshold - subtotal;
      const mount = document.querySelector(".rebuy-cart__widget, .rebuy-widget[data-location*='cart']");
      if (!mount || remaining <= 0) return;

      // Insert (or update) a small nudge
      let nudge = mount.querySelector(".swish-rebuy-nudge");
      if (!nudge) {
        nudge = document.createElement("div");
        nudge.className = "swish-rebuy-nudge";
        // Inherit theme styling as much as possible
        nudge.style.margin = "8px 0 6px";
        nudge.style.fontSize = "0.95em";
        mount.insertBefore(nudge, mount.firstChild);
      }
      nudge.textContent = `You’re $${remaining.toFixed(2)} away from free shipping — add a small item below.`;
    },

    async _availabilityCheck(lineItem) {
      // lineItem may contain: { variant_id, product_id, id, handle, tags, ... }
      const region = this.cfg.region || "us";
      if (this.cfg.availabilityMode === "allow_all") return true;
      if (region === "us") return true; // default: US allowed

      // STATIC JSON MODE: fetch once per session (cache)
      if (this.cfg.availabilityMode === "static_json") {
        try {
          const data = await this._fetchCached(this.cfg.availabilityEndpoint, "swish_avail_v1", 5 * 60 * 1000);
          if (!data) return true; // if no data, fail open
          const variantId = String(lineItem?.variant_id || lineItem?.id || "");
          const handle = (lineItem && lineItem.handle) || "";

          // Very light heuristic: allow if variantId or handle is present in region's map, otherwise true
          // Expecting schema like { regions: { ca: { allowedVariantIds: [..], denyHandles: [..] } } }
          const regions = data.regions || {};
          const rule = regions[region] || {};
          if (rule.allowedVariantIds && rule.allowedVariantIds.length) {
            if (!variantId) return true;
            return rule.allowedVariantIds.includes(variantId);
          }
          if (rule.denyHandles && rule.denyHandles.length && handle) {
            return !rule.denyHandles.includes(handle);
          }
          return true;
        } catch (e) {
          console.warn("[SWISH_REBUY] availability JSON fetch error", e);
          return true;
        }
      }

      // PROXY API MODE: call your backend (expects { ok: boolean })
      if (this.cfg.availabilityMode === "proxy_api") {
        try {
          const variantId = encodeURIComponent(String(lineItem?.variant_id || lineItem?.id || ""));
          const res = await fetch(`/apps/availability?variant_id=${variantId}&region=${encodeURIComponent(region)}`, { credentials: "same-origin" });
          if (!res.ok) return true;
          const j = await res.json();
          return !!j.ok;
        } catch (e) {
          console.warn("[SWISH_REBUY] availability proxy error", e);
          return true;
        }
      }

      return true;
    },

    async _fetchCached(url, cacheKey, ttlMs) {
      try {
        const now = Date.now();
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          const { t, v } = JSON.parse(cached);
          if (now - t < ttlMs) return v;
        }
        const resp = await fetch(url, { credentials: "omit", cache: "no-store" });
        if (!resp.ok) return null;
        const v = await resp.json();
        sessionStorage.setItem(cacheKey, JSON.stringify({ t: now, v }));
        return v;
      } catch {
        return null;
      }
    },

    _track(event, payload) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event, source: "rebuy" }, payload || {}));
      } catch (e) {
        // no-op
      }
    }
  };

  // Expose
  window.SWISH_REBUY = SWISH_REBUY;
})();
